/**
 * Filter navigation data by RBAC permissions.
 * Items without `permission` are always shown (e.g. SUPPORT items you leave ungated).
 * Items with `permission` are shown only if the user has that permission.
 */

export type NavItem = {
  title: string;
  path: string;
  icon?: React.ReactNode;
  permission?: string;
  children?: NavItem[];
};

export type NavGroup = {
  subheader: string | { title: string; subtitle?: string; info?: string; icon?: string | React.ReactNode };
  items: NavItem[];
};

export type HasPermissionFn = (permission: string) => boolean;

/**
 * Returns true if the item should be shown: no permission required, or user has the permission.
 */
function itemAllowed(item: NavItem, hasPermission: HasPermissionFn): boolean {
  if (!item.permission) return true;
  return hasPermission(item.permission);
}

/**
 * Filter a single nav item and its children. Removes children the user cannot access.
 */
function filterItem(item: NavItem, hasPermission: HasPermissionFn): NavItem | null {
  if (!itemAllowed(item, hasPermission)) return null;
  if (!item.children?.length) return { ...item };
  const filteredChildren = item.children
    .map((child) => filterItem(child, hasPermission))
    .filter((c): c is NavItem => c !== null);
  // Item exists only to host children (e.g. a grouping menu). If none of its
  // children are visible to this user, the item itself has nothing to show.
  if (filteredChildren.length === 0) return null;
  return { ...item, children: filteredChildren };
}

/**
 * Filter full nav data by user permissions.
 * - Items with no `permission` are shown to everyone.
 * - Items with `permission` are shown only if hasPermission(permission) is true.
 * - Groups with no visible items are omitted.
 * If hasPermission is not provided (e.g. RBAC not loaded), fails closed: only items that need
 * no permission are shown (this used to return the whole menu).
 */
export function filterNavByPermission(
  navData: NavGroup[],
  hasPermission: HasPermissionFn | null
): NavGroup[] {
  const allowed: HasPermissionFn = hasPermission ?? (() => false);

  return navData
    .map((group) => {
      const filteredItems = group.items
        .map((item) => filterItem(item, allowed))
        .filter((item): item is NavItem => item !== null);
      if (filteredItems.length === 0) return null;
      return { ...group, items: filteredItems };
    })
    .filter((g): g is NavGroup => g !== null);
}
